type FieldItem = {
  selectedSourceField: string | null;
};

type NestedField = FieldItem[][][];

export interface TransformedPreviewData {
  columns: Array<{
    field: string;
    headerName: string;
    flex: number;
  }>;
  rows: Array<Record<string, any>>;
}

/**
 * Transform lstFileData from preview API response
 * - First sub-array provides the keys (column headers)
 * - Subsequent sub-arrays provide values (row data)
 */
export function transformPreviewData(
  lstFileData: NestedField
): TransformedPreviewData {
  if (!lstFileData || lstFileData.length === 0) {
    return {columns: [], rows: []};
  }

  // Extract column headers from the first sub-array
  const headerFields = lstFileData[0].map(
    (fieldGroup) => fieldGroup[0]?.selectedSourceField ?? null
  );

  // Build columns for MUI DataGrid
  const columns = headerFields
    .filter((header): header is string => header !== null)
    .map((header, index) => {
      // Split by " => " to get display name
      const displayName = header;

      return {
        field: `col_${index}`, // Use col_0, col_1, etc. as field names
        headerName: displayName,
        flex: 1,
        headerClassName: 'typo-text label2_size',
      };
    });

  // Build rows from subsequent sub-arrays
  const rows: Array<Record<string, any>> = [];

  for (let i = 1; i < lstFileData.length; i++) {
    const valueFields = lstFileData[i];
    const row: Record<string, any> = {
      id: i, // MUI DataGrid requires unique 'id' field
    };

    headerFields.forEach((key, colIndex) => {
      const value = valueFields[colIndex]?.[0]?.selectedSourceField ?? null;
      row[`col_${colIndex}`] = value;
    });

    rows.push(row);
  }

  return {columns, rows};
}

/**
 * Alternative: Transform to simple key-value objects
 * (if you need generic object structure instead of DataGrid format)
 */
export function transformPreviewDataSimple(
  lstFileData: NestedField
): Array<Record<string, string | null>> {
  if (!lstFileData || lstFileData.length === 0) return [];

  // Extract keys from the first sub-array
  const keyFields = lstFileData[0].map(
    (fieldGroup) => fieldGroup[0]?.selectedSourceField ?? null
  );

  const result: Array<Record<string, string | null>> = [];

  // Iterate from the 2nd sub-array onwards
  for (let i = 1; i < lstFileData.length; i++) {
    const valueFields = lstFileData[i];
    const row: Record<string, string | null> = {};

    keyFields.forEach((key, index) => {
      const value = valueFields[index]?.[0]?.selectedSourceField ?? null;
      if (key !== null) {
        row[key] = value;
      }
    });

    result.push(row);
  }

  return result;
}

//test

type TransformedRow = Record<string, string | null>;

export const transformFileData = (
  lstFileData: NestedField
): TransformedRow[] => {
  if (!lstFileData || lstFileData.length === 0) return [];

  // Extract keys from the first sub-array
  const keyFields = lstFileData[0].map(
    (fieldGroup) => fieldGroup[0]?.selectedSourceField ?? null
  );

  const result: TransformedRow[] = [];

  // Iterate from the 2nd sub-array onwards (index 1, 2, ...)
  for (let i = 1; i < lstFileData.length; i++) {
    const valueFields = lstFileData[i];
    const row: TransformedRow = {};

    keyFields.forEach((key, index) => {
      const value = valueFields[index]?.[0]?.selectedSourceField ?? null;
      if (key !== null) {
        row[key] = value;
      }
    });

    result.push(row);
  }

  return result;
};
